import { Component, OnInit, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, Subject, combineLatest } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  selectHasPatrollingAssignment,
  selectAssignedGeofences,
  selectActivePatrolSession,
  selectPatrolStatistics,
  selectUnresolvedViolations,
  selectPatrolLoading,
} from 'src/app/state-management/selectors/guard-patrol.selectors';
import { IAumyeState } from 'src/app/state-management/states/aumye.state';
import { MatDialog } from '@angular/material/dialog';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { PatrolDetailDrawerComponent } from '../patrol-detail-drawer/patrol-detail-drawer.component';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { LoadUserPatrolData } from 'src/app/state-management/actions/guard-patrol.actions';
import { PatrolSession } from 'src/app/state-management/states/guard-patrol.state';
import { PatrolMonitoringService } from 'src/app/services/patrol-monitoring.service';

@Component({
  selector: 'app-patrolling-status',
  templateUrl: './patrolling-status.component.html',
  styleUrls: ['./patrolling-status.component.scss'],
})
export class PatrollingStatusComponent implements OnInit, OnDestroy {
  hasPatrollingAssignment$: Observable<boolean>;
  assignedGeofences$: Observable<any[]>;
  activePatrolSession$: Observable<PatrolSession>;
  patrolStatistics$: Observable<any>;
  unresolvedViolations$: Observable<any[]>;
  loading$: Observable<boolean>;

  hasAssignment = false;
  activeSession: PatrolSession | null = null;
  statistics: any = null;
  unresolvedViolations: any[] = [];

  // Monitoring data
  isMonitoring = false;
  totalChecks = 0;
  inZoneChecks = 0;
  complianceRate = 100;
  sessionDuration = '0m';

  private destroy$ = new Subject<void>();
  private durationInterval?: any;
  private isMobile = false;

  constructor(
    private store: Store<IAumyeState>,
    private dialog: MatDialog,
    private bottomSheet: MatBottomSheet,
    private patrolMonitoringService: PatrolMonitoringService,
    private breakpointObserver: BreakpointObserver
  ) {}

  ngOnInit(): void {
    // Check if mobile
    this.breakpointObserver
      .observe([Breakpoints.Handset])
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        this.isMobile = result.matches;
      });

    this.hasPatrollingAssignment$ = this.store.select(
      selectHasPatrollingAssignment
    );
    this.assignedGeofences$ = this.store.select(selectAssignedGeofences);
    this.activePatrolSession$ = this.store.select(selectActivePatrolSession);
    this.patrolStatistics$ = this.store.select(selectPatrolStatistics);
    this.unresolvedViolations$ = this.store.select(selectUnresolvedViolations);
    this.loading$ = this.store.select(selectPatrolLoading);

    this.hasPatrollingAssignment$
      .pipe(takeUntil(this.destroy$))
      .subscribe((hasAssignment) => {
        this.hasAssignment = hasAssignment;
      });

    combineLatest([this.activePatrolSession$, this.assignedGeofences$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([session, geofences]) => {
        const wasActive = !!this.activeSession;
        this.activeSession = session;

        // Auto-start monitoring when session exists
        if (session && geofences && geofences.length > 0) {
          if (true) {
            // Session just started, begin monitoring
            this.patrolMonitoringService.startMonitoring(
              session.id,
              geofences[0]
            );
            this.startDurationTimer();
          }
        } else if (!session && wasActive) {
          // Session ended
          this.stopDurationTimer();
        }
      });

    this.patrolStatistics$.pipe(takeUntil(this.destroy$)).subscribe((stats) => {
      this.statistics = stats;
    });

    this.unresolvedViolations$
      .pipe(takeUntil(this.destroy$))
      .subscribe((violations) => {
        this.unresolvedViolations = violations;
      });

    // Subscribe to monitoring status
    this.patrolMonitoringService
      .getMonitoringStatus()
      .pipe(takeUntil(this.destroy$))
      .subscribe((isMonitoring) => {
        this.isMonitoring = isMonitoring;
      });

    // Subscribe to checkpoint updates
    this.patrolMonitoringService
      .getCheckpoints()
      .pipe(takeUntil(this.destroy$))
      .subscribe((checkpoints) => {
        this.totalChecks = checkpoints.length;
        this.inZoneChecks = checkpoints.filter((c) => c.isInsideZone).length;
        this.complianceRate =
          this.totalChecks > 0
            ? (this.inZoneChecks / this.totalChecks) * 100
            : 100;
      });
  }

  private startDurationTimer(): void {
    this.stopDurationTimer();
    this.updateSessionDuration();
    this.durationInterval = setInterval(() => {
      this.updateSessionDuration();
    }, 30000); // Update every 30 seconds
  }

  private stopDurationTimer(): void {
    if (this.durationInterval) {
      clearInterval(this.durationInterval);
      this.durationInterval = undefined;
    }
  }

  private updateSessionDuration(): void {
    if (!this.activeSession?.startedAt) {
      this.sessionDuration = '0m';
      return;
    }
    const start = new Date(this.activeSession.startedAt + 'Z').getTime();
    const now = new Date().getTime();
    const diffMs = now - start;
    const diffMins = Math.floor(diffMs / 60000);
    const hours = Math.floor(diffMins / 60);
    const mins = diffMins % 60;

    if (hours > 0) {
      this.sessionDuration = `${hours}h ${mins}m`;
    } else {
      this.sessionDuration = `${mins}m`;
    }
  }

  openDetailsDrawer(): void {
    if (this.isMobile) {
      // Use bottom sheet for mobile
      this.bottomSheet.open(PatrolDetailDrawerComponent, {
        panelClass: 'patrol-detail-bottom-sheet',
        // data: {
        //   hasAssignment: this.hasAssignment,
        //   activeSession: this.activeSession,
        //   statistics: this.statistics,
        //   unresolvedViolations: this.unresolvedViolations,
        // },
      });
    } else {
      // Use dialog for desktop
      this.dialog.open(PatrolDetailDrawerComponent, {
        width: '600px',
        maxWidth: '90vw',
        maxHeight: '90vh',
        panelClass: 'patrol-detail-dialog',
        // data: {
        //   hasAssignment: this.hasAssignment,
        //   activeSession: this.activeSession,
        //   statistics: this.statistics,
        //   unresolvedViolations: this.unresolvedViolations,
        // },
      });
    }
  }

  onRefreshPatrolData(): void {
    this.store.dispatch(new LoadUserPatrolData());
  }

  ngOnDestroy(): void {
    this.stopDurationTimer();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
